import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-coverage-list>` says about itself.
 *
 * Coverage is the number on the Unit Tests screen that gates a deploy, so this list is read for a
 * decision rather than for information: what is below the bar, and by how much.
 *
 * Two decisions follow, and both are worth knowing before using it.
 *
 * It orders WORST FIRST. A list sorted by name buries three failing artifacts among two hundred
 * passing ones, so the reader's actual question takes a scan to answer. `order="name"` is there for
 * looking up an artifact somebody can already name — a different task.
 *
 * An artifact with no executable lines is NOT zero percent. An interface, a constants file, a
 * type-only module has nothing to cover; scoring it zero pins it to the top of a worst-first list
 * forever while nothing is wrong, which is exactly how a coverage list trains people to ignore it.
 * Those rows read "no lines", sort to the end, and are counted in neither the shortfall nor the
 * measured total.
 *
 * Note the direction: coverage is the inverse of consumption. The bars are told their intent
 * explicitly rather than using `<caos-usage-bar>`'s own thresholds, which would redden a
 * well-covered artifact.
 */
const docs: ComponentDocs = {
  aliases: ['code coverage', 'coverage report', 'coverage table', 'test coverage list', 'coverage by class'],

  answers: [
    'show which classes are below the coverage requirement',
    'see how far short of the bar a deploy is',
    'find the least-covered code first',
    'look up one artifact’s coverage by name',
  ],

  placements: ['appPage', 'recordPage'],

  whenToUse:
    'On a test-results surface, where coverage gates something and the reader needs to know what is ' +
    'blocking them.',

  insteadUse: [
    {
      name: 'DataTable',
      when:
        'coverage is one column among many being sorted, filtered and exported — a table is for ' +
        'comparing values, this list is for finding what is short.',
    },
    {
      name: 'UsageBar',
      when: 'there is a single aggregate coverage figure rather than a per-artifact breakdown.',
    },
  ],

  inputs: [
    {
      name: 'entries',
      type: 'json',
      description:
        'The artifacts to list: `{ name, covered, total }[]`, where the two numbers are LINE COUNTS ' +
        'rather than a pre-computed percentage — the component derives the percentage so that "no ' +
        'executable lines" stays distinguishable from "zero percent covered". A property, because it ' +
        'is structured data.',
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'number',
      description: 'The coverage floor, in percent. Rows below it are marked in text as well as colour.',
      default: 75,
    },
    {
      name: 'order',
      attribute: 'order',
      type: 'text',
      options: ['coverage', 'name'],
      description:
        'How the rows are ordered. `coverage` (the default) is worst-first, for deciding what is ' +
        'blocking a deploy; `name` is alphabetical, for looking up a known artifact.',
      default: 'coverage',
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say when no coverage has been reported.',
      default: 'No coverage reported.',
    },
  ],

  state: [
    {
      name: 'below',
      type: 'CaosCoverageEntry[]',
      description:
        'The artifacts under the floor, worst first. Uncoverable artifacts are absent by construction ' +
        '— they have no percentage to be below anything. Worst first whatever `order` is set to, ' +
        'because this is a work list rather than a view. Read it to gate an action, or to drive a ' +
        '“fix these” list elsewhere, without recomputing the rule.',
    },
    {
      name: 'required',
      type: 'number',
      description: 'The floor currently in force, resolved from the attribute or the default.',
    },
  ],

  parts: [
    { part: 'list', is: 'The outer column.' },
    { part: 'summary', is: 'The "N below X%" verdict line.' },
    { part: 'row', is: 'One artifact’s row.' },
    { part: 'name', is: 'The artifact name.' },
    { part: 'bar', is: 'The coverage bar in a row.' },
    { part: 'percent', is: 'The percentage, or "no lines".' },
  ],

  accessibility:
    'The summary is a `role="status"` live region, so a completed run that drops an artifact below the ' +
    'floor is announced rather than only redrawn. Each row carries an `aria-label` reading the name, ' +
    'the percentage, the line counts and — when it applies — that it is below the requirement, so ' +
    'being short is spoken rather than conveyed only by the red. Uncoverable rows say "no executable ' +
    'lines" rather than reading as zero.',

  examples: [
    {
      key: 'worst-first',
      label: 'What is blocking the deploy',
      summary:
        'The default order. Three artifacts are short of the floor and they are the first three rows, ' +
        'so the question the screen is open to answer is answered without scanning.',
      placement: {
        component: 'caos-coverage-list',
        inputs: {
          entries: [
            { name: 'BillingService', covered: 412, total: 455 },
            { name: 'AccountTrigger', covered: 31, total: 104 },
            { name: 'InvoiceHelper', covered: 148, total: 200 },
            { name: 'QuoteCalculator', covered: 66, total: 120 },
            { name: 'OrderSyncBatch', covered: 290, total: 300 },
          ],
        },
      },
      stage: { needsWidth: 520 },
    },
    {
      key: 'uncoverable',
      label: 'An artifact with nothing to cover',
      summary:
        'A constants file and a type-only module have no executable lines. They read “no lines”, sort ' +
        'to the END of a worst-first list, and count in neither the shortfall nor the measured total — ' +
        'scoring them zero would pin them to the top for ever while nothing was wrong.',
      placement: {
        component: 'caos-coverage-list',
        inputs: {
          entries: [
            { name: 'PricingConstants', covered: 0, total: 0 },
            { name: 'BillingTypes', covered: 0, total: 0 },
            { name: 'AccountTrigger', covered: 31, total: 104 },
            { name: 'OrderSyncBatch', covered: 290, total: 300 },
          ],
        },
      },
      stage: { needsWidth: 520 },
    },
    {
      key: 'raised-floor',
      label: 'A stricter requirement',
      summary:
        'The same artifacts against a 90% floor. The shortfall is a function of the requirement, not a ' +
        'property of the numbers, so moving the floor moves the count and the colours together.',
      placement: {
        component: 'caos-coverage-list',
        inputs: {
          required: 90,
          entries: [
            { name: 'BillingService', covered: 412, total: 455 },
            { name: 'InvoiceHelper', covered: 148, total: 200 },
            { name: 'OrderSyncBatch', covered: 290, total: 300 },
          ],
        },
      },
      stage: { needsWidth: 520 },
    },
    {
      key: 'by-name',
      label: 'Looking one up',
      summary:
        'Alphabetical, for the other task: somebody who can already name the artifact and wants its ' +
        'number. The shortfall read off `below` stays worst-first regardless — it is a work list.',
      placement: {
        component: 'caos-coverage-list',
        inputs: {
          order: 'name',
          entries: [
            { name: 'BillingService', covered: 412, total: 455 },
            { name: 'AccountTrigger', covered: 31, total: 104 },
            { name: 'InvoiceHelper', covered: 148, total: 200 },
          ],
        },
      },
      stage: { needsWidth: 520 },
    },
  ],
};

export default docs;
